import React, { useMemo } from 'react';
import styled from 'styled-components';
import { createSelector } from '@reduxjs/toolkit';

import type { IWeek, IWeekDay } from '@/types/calendar';
import type { ISortedGroupedSchedule, ISortedShift } from '@/types/schedule';
import type { RootState } from '@/store';

import { classList } from '@/helpers';
import { PADDING_WEEKDAY, Z_INDEX_SHIFT_ROW } from '@/config/variables';
import { useAppSelector, useCalendarShifts } from '@/hooks';
import { ShiftColumn } from '@/components/agent/my-schedule/ShiftColumn';
import { selectSelectedSchedulesIds } from '@/store/selectors/scheduleSelectors';
import { selectActiveModalShiftId } from '@/store/selectors/utilsSelectors';
import { AllShiftItemType } from '@/store/enums';

interface IProps {
  row: ISortedShift[][];
  day: IWeekDay;
  week: IWeek;
}

const shiftRowSelector = createSelector(
  selectSelectedSchedulesIds,
  selectActiveModalShiftId,
  (_: RootState, column: ISortedShift[]) => column,
  (selectedSchedulesIds, activeModalShiftId, column) => ({
    selectedSchedulesIds,
    hasActiveModal: column.some(
      item =>
        item.itemData.itemType === AllShiftItemType.schedule &&
        (item as ISortedShift<ISortedGroupedSchedule>).shifts.some(shift => shift.shiftId === activeModalShiftId),
    ),
  }),
);

const ShiftRow = ({ row, week, day }: IProps): React.ReactElement => {
  const rowColumns = useMemo(() => row.flat(), [row]);
  const { selectedSchedulesIds, hasActiveModal } = useAppSelector(state => shiftRowSelector(state, rowColumns));
  const { getRowTopPosition } = useCalendarShifts({ day, week });

  const nonEmptyColumnsLength = useMemo(
    () =>
      row.filter(column => column.length > 0 && column.filter(shift => selectedSchedulesIds.includes(shift.scheduleId)))
        .length,
    [row, selectedSchedulesIds],
  );

  const rowTop = useMemo(() => getRowTopPosition(row), [getRowTopPosition, row]);

  return (
    <Row
      top={rowTop}
      data-row-length={nonEmptyColumnsLength}
      key={row[0][0].itemData.id + row[0][0].displayBlocks.startBlock}
      className={classList(hasActiveModal && 'has-active-modal')}
    >
      {row.map((column, columnIndex) => (
        <ShiftColumn
          key={column[0].itemData.id}
          column={column}
          day={day}
          week={week}
          rowTop={rowTop}
          index={columnIndex}
          row={row}
          hasActiveModal={hasActiveModal}
        />
      ))}
    </Row>
  );
};

const MemoizedShiftRow = React.memo(ShiftRow);
export { MemoizedShiftRow as ShiftRow };

const Row = styled.div<{ top: number }>`
  display: flex;
  flex-direction: row;
  flex-grow: 1;
  width: 100%;
  padding-left: ${PADDING_WEEKDAY}px;
  padding-right: ${PADDING_WEEKDAY}px;
  position: absolute;
  left: 0;
  top: ${props => props.top}px;
  z-index: ${Z_INDEX_SHIFT_ROW};

  &.has-active-modal {
    z-index: ${Z_INDEX_SHIFT_ROW + 1};
  }
`;
